import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  CircularProgress,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { AdminKeyService } from '../../services/adminKeyService';

type CampaignDraft = {
  id: string;
  name: string;
  status?: 'draft' | 'published';
  subject: string;
  fromEmail: string;
  fromName: string;
  replyToEmail: string;
  groupId?: string;
  text: string;
  html: string;
  createdAt?: string;
  updatedAt?: string;
};

type CampaignDraftListItem = {
  id: string;
  name?: string;
  subject?: string;
  status?: string;
  updatedAt?: string | null;
  createdAt?: string | null;
};

type PlatformGroup = {
  id: string;
  name: string;
};

const makeEmptyDraft = (): CampaignDraft => {
  const id = crypto?.randomUUID ? crypto.randomUUID() : `draft_${Math.random().toString(16).slice(2)}_${Date.now()}`;
  return {
    id,
    name: 'New Campaign',
    status: 'draft',
    subject: '',
    fromEmail: '',
    fromName: '',
    replyToEmail: '',
    groupId: '',
    text: '',
    html: '<div style="font-family:Arial,sans-serif">\n  <h2>Hello!</h2>\n  <p>Write your campaign here.</p>\n</div>',
  };
};

const PlatformCampaignsTab: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [items, setItems] = React.useState<CampaignDraftListItem[]>([]);
  const [selectedId, setSelectedId] = React.useState<string>('');
  const [draft, setDraft] = React.useState<CampaignDraft | null>(null);

  const [campaignTab, setCampaignTab] = React.useState<'html' | 'text' | 'preview'>('html');
  const autosaveTimerRef = React.useRef<number | null>(null);

  const [groups, setGroups] = React.useState<PlatformGroup[]>([]);

  const callApi = React.useCallback(async (action: string, payload?: any) => {
    const token = await AdminKeyService.getSecret();
    if (!token) throw new Error('Missing admin token');

    const res = await fetch('/api/mail-campaigns.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ action, ...(payload || {}) }),
    });

    const raw = await res.text();
    let json: any = null;
    try {
      json = raw ? JSON.parse(raw) : null;
    } catch {
      json = null;
    }

    if (!res.ok || !json?.ok) {
      const msg = json?.error || raw || `${res.status} ${res.statusText}`;
      throw new Error(msg);
    }

    return json;
  }, []);

  const callGroupsApi = React.useCallback(async (action: string, payload?: any) => {
    const token = await AdminKeyService.getSecret();
    if (!token) throw new Error('Missing admin token');

    const res = await fetch('/api/platform-groups.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ action, ...(payload || {}) }),
    });

    const raw = await res.text();
    let json: any = null;
    try {
      json = raw ? JSON.parse(raw) : null;
    } catch {
      json = null;
    }

    if (!res.ok || !json?.ok) {
      const msg = json?.error || raw || `${res.status} ${res.statusText}`;
      throw new Error(msg);
    }

    return json;
  }, []);

  const loadList = React.useCallback(async () => {
    try {
      setLoading(true);
      const json = await callApi('list');
      setItems(Array.isArray(json?.items) ? json.items : []);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [callApi]);

  const deleteDraft = async (id: string) => {
    try {
      setSaving(true);
      await callApi('delete', { id });
      if (selectedId === id) {
        setSelectedId('');
        setDraft(null);
      }
      await loadList();
      toast.success('Deleted');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => {
    loadList();
  }, [loadList]);

  const loadGroups = React.useCallback(async () => {
    try {
      const json = await callGroupsApi('list');
      const list = Array.isArray(json?.items) ? json.items : [];
      setGroups(
        list
          .map((g: any) => ({ id: String(g?.id || ''), name: String(g?.name || '') }))
          .filter((g: PlatformGroup) => g.id && g.name)
      );
    } catch {
      setGroups([]);
    }
  }, [callGroupsApi]);

  React.useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const loadDraft = React.useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        const json = await callApi('load', { id });
        const d = json?.draft as CampaignDraft;
        if (!d?.id) throw new Error('Draft missing id');
        setSelectedId(String(d.id));
        setDraft(d);
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load draft');
      } finally {
        setLoading(false);
      }
    },
    [callApi]
  );

  const createNew = async () => {
    const d = makeEmptyDraft();
    try {
      setSaving(true);
      await callApi('save', { draft: d });
      setSelectedId(d.id);
      setDraft(d);
      await loadList();
      toast.success('Draft created');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create draft');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!draft) {
      toast.error('Select or create a draft first');
      return;
    }

    try {
      setSaving(true);
      await callApi('save', { draft });
      await loadList();
      toast.success('Saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveDraftDebounced = React.useCallback(
    (next: CampaignDraft) => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
      }
      autosaveTimerRef.current = window.setTimeout(() => {
        callApi('save', { draft: next })
          .then(() => loadList())
          .catch(() => {
            // best-effort autosave
          });
      }, 800);
    },
    [callApi, loadList]
  );

  React.useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    };
  }, []);

  const updateDraft = (patch: Partial<CampaignDraft>) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      saveDraftDebounced(next);
      return next;
    });
  };

  const fmtWhen = (iso?: string | null) => {
    if (!iso) return '';
    const t = Date.parse(String(iso));
    if (!Number.isFinite(t)) return String(iso);
    try {
      return new Date(t).toLocaleString();
    } catch {
      return String(iso);
    }
  };

  return (
    <Box>
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CampaignIcon fontSize="small" />
            <Typography fontWeight={900}>Campaign Drafts</Typography>
            {saving && <CircularProgress size={16} />}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={loadList} disabled={loading || saving} startIcon={<RefreshIcon />}>
              Reload
            </Button>
            <Button variant="contained" onClick={createNew} disabled={loading || saving} startIcon={<AddIcon />}>
              New draft
            </Button>
          </Box>
        </Box>
        <Divider />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '360px 1fr' },
            minHeight: 460,
          }}
        >
          <Box sx={{ borderRight: { md: '1px solid' }, borderColor: { md: 'divider' } }}>
            <Box sx={{ position: 'relative' }}>
              {loading && (
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}>
                  <LinearProgress />
                </Box>
              )}
              <List dense disablePadding sx={{ maxHeight: { xs: 260, md: 460 }, overflow: 'auto' }}>
                {items.length === 0 && !loading && (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">No drafts yet</Typography>
                  </Box>
                )}
                {items.map((d) => (
                  <ListItemButton
                    key={d.id}
                    selected={String(d.id) === String(selectedId)}
                    onClick={() => loadDraft(String(d.id))}
                    sx={{ alignItems: 'flex-start', py: 1.1 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Typography fontWeight={900} fontSize={13} noWrap>{d.name || 'Draft'}</Typography>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteDraft(String(d.id)); }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {d.subject ? `Subject: ${d.subject}` : 'No subject yet'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {fmtWhen(d.updatedAt || d.createdAt) || ''}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Box>

          <Box sx={{ p: 2 }}>
            {!draft ? (
              <Box sx={{ p: 2 }}>
                <Typography fontWeight={900} gutterBottom>Select a draft</Typography>
                <Typography variant="body2" color="text.secondary">Create a new draft or select one from the left.</Typography>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography fontWeight={900} fontSize={16}>{draft.name || 'Draft'}</Typography>
                    <Typography variant="caption" color="text.secondary">Autosaves to <code>storage/mail-campaigns</code></Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={save}
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                    >
                      Save now
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    label="Draft name"
                    value={draft.name}
                    onChange={(e) => updateDraft({ name: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Subject"
                    value={draft.subject}
                    onChange={(e) => updateDraft({ subject: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="From email"
                    value={draft.fromEmail}
                    onChange={(e) => updateDraft({ fromEmail: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="From name"
                    value={draft.fromName}
                    onChange={(e) => updateDraft({ fromName: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Reply-to email"
                    value={draft.replyToEmail}
                    onChange={(e) => updateDraft({ replyToEmail: e.target.value })}
                    fullWidth
                  />
                  <FormControl fullWidth>
                    <InputLabel id="platform-campaign-group">Target group</InputLabel>
                    <Select
                      labelId="platform-campaign-group"
                      label="Target group"
                      value={draft.groupId || ''}
                      onChange={(e) => updateDraft({ groupId: String(e.target.value || '') || undefined })}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {groups.map((g) => (
                        <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Optional</FormHelperText>
                  </FormControl>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Tabs
                  value={campaignTab}
                  onChange={(_, v) => setCampaignTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 1 }}
                >
                  <Tab value="html" label="HTML" />
                  <Tab value="text" label="Text" />
                  <Tab value="preview" label="Preview" />
                </Tabs>

                {campaignTab === 'html' && (
                  <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    label="HTML"
                    value={draft.html}
                    onChange={(e) => updateDraft({ html: e.target.value })}
                    sx={{ fontFamily: 'monospace' }}
                  />
                )}

                {campaignTab === 'text' && (
                  <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    label="Plain text"
                    value={draft.text}
                    onChange={(e) => updateDraft({ text: e.target.value })}
                  />
                )}

                {campaignTab === 'preview' && (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'background.default',
                    }}
                  >
                    <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Preview (sandboxed)</Typography>
                      <Chip size="small" label={draft.subject ? `Subject: ${draft.subject}` : 'No subject'} />
                    </Box>
                    <Box sx={{ height: 360 }}>
                      <iframe
                        title="Campaign preview"
                        sandbox="allow-same-origin"
                        style={{ width: '100%', height: '100%', border: 0, background: 'white' }}
                        srcDoc={draft.html || '<div style="font-family:Arial,sans-serif;padding:16px;color:#666">No HTML yet</div>'}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default PlatformCampaignsTab;
